import {
  AgendaSpeaker,
  PublicEventDetailsResponse,
  PublicExhibitionItem,
  PublicSessionItem,
  Portfolio,
  ResponsePaginationType,
  PublicAttendee,
} from '@hubs101/booking-api-skd-client/lib/types';
import { Document, Image, Link, Text, View } from '@react-pdf/renderer';
import { useCallback, useMemo } from 'react';
import { APP_HOST } from '@utils/apiHooks';

import worldIcon from '@assets/icons/world_icon.png';
import worldIconBlack from '@assets/icons/world_icon_black.png';
import mailIcon from '@assets/icons/mail_icon.png';
import mailIconBlack from '@assets/icons/mail_icon_black.png';
import { useTranslation } from 'react-i18next';
import { DefaultTheme } from 'styled-components';
import { styles } from './styles';

import TitlePage from './pages/TitlePage';
import ClaimPage from './pages/ClaimPage';
import { MULTI_TOUCHPOINT_ITEMS, SPONSOR_EMAIL } from '@utils/constants';
import WhoAndWhyPage from './pages/WhoAndWhyPage';
import MatchMakingPage from './pages/MatchMakingPage';
import MultitouchpointPage from './pages/MultitouchpointPage';
import KeyTopicsPage from './pages/KeyTopicsPage';
import NETPromoterScorePage from './pages/NETPromoterScorePage';
import ListOfAttendeesPage from './pages/ListOfAttendeesPage';
import CoLocatedEventsPage from './pages/CoLocatedEventsPage';
import AllSpeakerPage from './pages/AllSpeakerPage';
import PartnerWithUsPage from './pages/PartnerWithUsPage';
import PartnerThisYearPage from './pages/PartnerThisYearPage';
import { Ticket } from '@composites/Tickets';
import PackagePage from './pages/PackagePage';
import CommunityFeedbackPage from './pages/CommunityFeedbackPage';
import EventSeriesPage from './pages/EventSeriesPage';
import { getImageSrc, toNumber } from '@utils/fn';
import KeynotePage from './pages/KeynotePage';

export async function imageToWhite(imageUrl: string): Promise<string> {
  return await new Promise(resolve => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;

      ctx.drawImage(img as unknown as CanvasImageSource, 0, 0);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      for (let i = 0; i < data.length; i += 4) {
        // overwrite RGB, keep alpha
        data[i] = 255; // R
        data[i + 1] = 255; // G
        data[i + 2] = 255; // B
      }

      ctx.putImageData(imageData, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
  });
}

export async function imageToBlack(imageUrl: string): Promise<string> {
  return await new Promise(resolve => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;

      ctx.drawImage(img as unknown as CanvasImageSource, 0, 0);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      for (let i = 0; i < data.length; i += 4) {
        data[i] = 0; // R
        data[i + 1] = 0; // G
        data[i + 2] = 0; // B
      }

      ctx.putImageData(imageData, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
  });
}

export type ImageColorMode = 'white' | 'black' | 'original';

export async function resolveImageColor(
  src: string,
  mode?: ImageColorMode
): Promise<string> {
  switch (mode) {
    case 'white':
      return await imageToWhite(src);

    case 'black':
      return await imageToBlack(src);

    case 'original':
    default:
      return await Promise.resolve(src);
  }
}

interface BusinessPartnerPdfContentProps {
  event: PublicEventDetailsResponse;
  coLocatedEvents: PublicEventDetailsResponse[] | undefined;
  portfolios: ResponsePaginationType<Portfolio> | undefined;
  theme: DefaultTheme;
  groupedAgendasSessions: PublicSessionItem[][];
  agendaPdfDates: string[];
  agendaSpeakers: AgendaSpeaker[];
  exhibitions: PublicExhibitionItem[];
  advertisements: any[] | undefined;
  attendees: PublicAttendee[] | undefined;
  tickets?: Ticket[];
  allTickets: Ticket[];
  eventSeries: PublicEventDetailsResponse[];
}

const BusinessPartnerPdfContent = ({
  event,
  coLocatedEvents,
  portfolios,
  groupedAgendasSessions,
  agendaPdfDates,
  agendaSpeakers,
  exhibitions,
  advertisements,
  attendees,
  allTickets,
  eventSeries,
}: BusinessPartnerPdfContentProps) => {
  const { t } = useTranslation();

  const currentPortfolio = portfolios?.data?.find(
    p => p.slug === event?.portfolio?.slug
  );
  const portfolioUrl = currentPortfolio?.slug
    ? `${APP_HOST}${t('mappable-company-routes.our-product-portfolio-url')}/${currentPortfolio.slug}`
    : '';

  const portfolioDesign = (currentPortfolio?.design ?? {}) as any;

  const seriesLogoNeg = portfolioDesign?.logoNegImagePng
    ? getImageSrc(portfolioDesign.logoNegImagePng)
    : undefined;
  const seriesLogoPos = portfolioDesign?.logoPosImagePng
    ? getImageSrc(portfolioDesign.logoPosImagePng)
    : undefined;

  const Footer = useCallback(
    ({
      leftLightMode,
      rightLightMode,
      fontSize = 14,
      backgroundColor,
      textColor,
      modeLeft = 'original',
      modeRight = 'original',
      paddingLeft = undefined,
    }: {
      leftLightMode?: boolean;
      rightLightMode?: boolean;
      fontSize?: number;
      backgroundColor?: string;
      textColor?: string;
      modeLeft?: ImageColorMode;
      modeRight?: ImageColorMode;
      paddingLeft?: number | undefined;
    }) => {
      return (
        <View
          style={{
            ...styles.footer,
            backgroundColor:
              backgroundColor ||
              (leftLightMode === rightLightMode && leftLightMode === false
                ? event?.design?.themeColor
                : 'transparent'),
            width:
              leftLightMode === rightLightMode && leftLightMode === false
                ? '98%'
                : '100%',
            marginLeft:
              leftLightMode === rightLightMode && leftLightMode === false
                ? 15
                : 0,
          }}
          fixed
        >
          <View
            style={{
              ...styles.footerColumn,
              backgroundColor:
                backgroundColor ||
                (leftLightMode ? 'white' : event?.design?.themeColor),
              borderTopLeftRadius:
                leftLightMode === rightLightMode ? '15px' : 0,
              color: leftLightMode ? '#27343d' : 'white',
              alignItems: 'flex-start',
              paddingLeft:
                leftLightMode === rightLightMode && leftLightMode === false
                  ? (paddingLeft ?? 38)
                  : 53,
            }}
          >
            <Link src={portfolioUrl} style={{ textDecoration: 'none' }}>
              {(seriesLogoNeg ?? seriesLogoPos) ? (
                <Image
                  style={styles.logo}
                  src={resolveImageColor(
                    leftLightMode
                      ? (seriesLogoPos ?? seriesLogoNeg ?? '')
                      : (seriesLogoNeg ?? seriesLogoPos ?? ''),
                    modeLeft
                  )}
                />
              ) : null}
            </Link>
          </View>

          <View
            style={{
              ...styles.footerColumn,
              backgroundColor:
                backgroundColor ||
                (leftLightMode ? 'white' : event?.design?.themeColor),
              color: leftLightMode ? '#27343d' : 'white',
            }}
          >
            <View
              style={{
                ...styles.footerMiddleItem,
                color: leftLightMode ? '#27343d' : 'white',
                fontSize: fontSize,
                marginBottom: 5,
              }}
            >
              <Image
                src={
                  textColor === '#ffffff'
                    ? worldIcon
                    : leftLightMode
                      ? worldIconBlack
                      : worldIcon
                }
                style={styles.icon}
              />
              {portfolioUrl ? (
                <Link
                  src={portfolioUrl}
                  style={{
                    color: textColor || (leftLightMode ? '#27343d' : 'white'),
                    textDecoration: 'none',
                  }}
                >
                  {t('view-event-series-here')}
                </Link>
              ) : (
                <Text
                  style={{
                    color: textColor || (leftLightMode ? '#27343d' : 'white'),
                    textDecoration: 'none',
                  }}
                >
                  {t('view-event-series-here')}
                </Text>
              )}
            </View>
          </View>

          <View
            style={{
              ...styles.footerColumn,
              backgroundColor:
                backgroundColor ||
                (rightLightMode ? 'white' : event?.design?.themeColor),
              color: rightLightMode ? '#27343d' : 'white',
            }}
          >
            <View
              style={{
                ...styles.footerMiddleItem,
                backgroundColor:
                  backgroundColor ||
                  (rightLightMode ? 'white' : event?.design?.themeColor),
                color: rightLightMode ? '#27343d' : 'white',
                fontSize: fontSize,
                marginBottom: 5,
              }}
            >
              <Image
                src={
                  textColor === '#ffffff'
                    ? mailIcon
                    : rightLightMode
                      ? mailIconBlack
                      : mailIcon
                }
                style={styles.icon}
              />
              <Link
                href={`mailto:${SPONSOR_EMAIL}`}
                style={{
                  color: textColor || (rightLightMode ? '#27343d' : 'white'),
                  textDecoration: 'none',
                }}
              >
                {SPONSOR_EMAIL}
              </Link>
            </View>
          </View>

          <View
            style={{
              ...styles.footerColumn,
              backgroundColor:
                backgroundColor ||
                (rightLightMode ? 'white' : event?.design?.themeColor),
              borderTopRightRadius:
                leftLightMode === rightLightMode ? '15px' : 0,
              color: rightLightMode ? '#27343d' : 'white',
              alignItems: 'flex-end',
              paddingRight:
                leftLightMode === rightLightMode && leftLightMode === false
                  ? 38
                  : 53,
            }}
          >
            <Link
              src={`${APP_HOST}/events/${event?.slug}`}
              style={{ textDecoration: 'none' }}
            >
              <Image
                style={styles.logo}
                src={resolveImageColor(
                  getImageSrc(event?.images?.logoTransparent),
                  modeRight
                )}
              />
            </Link>
          </View>
        </View>
      );
    },
    [event?.design?.themeColor, portfolioUrl, t]
  );

  function getPackageCurrency(pkg: any) {
    const ticket = allTickets.find(t => t.package === pkg?.slug);
    const curr = ticket?.currency ? ticket.currency : 'eur';

    return curr;
  }

  const account: any = currentPortfolio?.account;

  const packages = (account?.packages || [])?.filter(
    (p: any) => p?.type === 'bp'
  );
  const orderedPackages = (packages ?? [])
    .slice()
    .sort((a: any, b: any) => toNumber(b?.value) - toNumber(a?.value));

  const keynoteSessions: PublicSessionItem[] = groupedAgendasSessions
    ?.flat()
    ?.filter((session: PublicSessionItem) => {
      return session?.type === 'keynote';
    })
    ?.filter((session: PublicSessionItem) => {
      if (event?.previous_agenda_event_id) {
        return session.event === event?.previous_agenda_event_id;
      } else {
        return session.event === event?.id;
      }
    });

  return (
    <Document>
      <TitlePage
        event={event}
        footer={
          <Footer
            leftLightMode={false}
            rightLightMode={false}
            fontSize={12}
            paddingLeft={53}
            modeLeft="white"
            modeRight="white"
          />
        }
      />

      <ClaimPage
        event={event}
        footer={
          <Footer
            leftLightMode={false}
            rightLightMode={false}
            backgroundColor="transparent"
            modeLeft="white"
            modeRight="white"
          />
        }
      />

      <WhoAndWhyPage
        event={event}
        footer={
          <Footer
            leftLightMode={true}
            rightLightMode={false}
            modeLeft="black"
            modeRight="white"
          />
        }
      />

      <MatchMakingPage
        event={event}
        footer={
          <Footer
            leftLightMode={false}
            rightLightMode={false}
            modeLeft="white"
            modeRight="white"
          />
        }
      />

      <MultitouchpointPage
        event={event}
        footer={
          <Footer
            leftLightMode={false}
            rightLightMode={false}
            modeLeft="white"
            modeRight="white"
          />
        }
        paragraphs={[
          'multitouchpoint-paragraph-1',
          'multitouchpoint-paragraph-2',
        ]}
        multiTouchPointItems={MULTI_TOUCHPOINT_ITEMS(t, event?.slug).slice(
          0,
          5
        )}
      />

      <MultitouchpointPage
        event={event}
        footer={
          <Footer
            leftLightMode={false}
            rightLightMode={false}
            modeLeft="white"
            modeRight="white"
          />
        }
        paragraphs={[
          'multitouchpoint-paragraph-3',
          'multitouchpoint-paragraph-4',
        ]}
        multiTouchPointItems={MULTI_TOUCHPOINT_ITEMS(t, event?.slug).slice(
          5,
          10
        )}
        inverse={true}
      />

      <KeynotePage
        event={event}
        seriesLogoNeg={seriesLogoNeg ?? ''}
        seriesLogoPos={seriesLogoPos ?? ''}
        keynoteSessions={keynoteSessions}
      />

      <KeyTopicsPage
        event={event}
        footer={
          <Footer
            leftLightMode={true}
            rightLightMode={true}
            backgroundColor="transparent"
            textColor="#ffffff"
            modeLeft="black"
            modeRight="white"
          />
        }
      />

      <CoLocatedEventsPage
        event={event}
        coLocatedEvents={coLocatedEvents}
        footer={
          <Footer
            leftLightMode={false}
            rightLightMode={false}
            modeLeft="white"
            modeRight="white"
          />
        }
      />

      <PartnerThisYearPage
        event={event}
        footer={
          <Footer
            leftLightMode={true}
            rightLightMode={true}
            backgroundColor="#F5F5F5"
            modeLeft="black"
            modeRight="black"
          />
        }
        exhibitions={exhibitions}
      />

      <ListOfAttendeesPage
        event={event}
        footer={
          <Footer
            leftLightMode={false}
            rightLightMode={false}
            modeLeft="white"
            modeRight="white"
          />
        }
      />

      <AllSpeakerPage
        event={event}
        footer={
          <Footer
            leftLightMode={false}
            rightLightMode={false}
            modeLeft="white"
            modeRight="white"
          />
        }
        agendaSpeakers={agendaSpeakers}
      />
      <CommunityFeedbackPage
        event={event}
        footer={
          <Footer
            leftLightMode={false}
            rightLightMode={false}
            modeLeft="white"
            modeRight="white"
          />
        }
      />

      <NETPromoterScorePage
        event={event}
        footer={
          <Footer
            leftLightMode={false}
            rightLightMode={false}
            modeLeft="white"
            modeRight="white"
          />
        }
      />

      <PartnerWithUsPage
        event={event}
        footer={
          <Footer
            leftLightMode={false}
            rightLightMode={true}
            backgroundColor="transparent"
            modeLeft="white"
            modeRight="black"
          />
        }
      />

      {orderedPackages?.map((p: any) => {
        return (
          <PackagePage
            key={p?.slug}
            event={event}
            coLocatedEvents={coLocatedEvents}
            packageItem={p}
            currency={getPackageCurrency(p)}
            footer={
              <Footer
                leftLightMode={true}
                rightLightMode={true}
                modeLeft="black"
                modeRight="black"
              />
            }
          />
        );
      })}

      <EventSeriesPage
        event={event}
        eventSeries={eventSeries}
        portfolio={currentPortfolio}
        footer={
          <Footer
            leftLightMode={true}
            rightLightMode={true}
            backgroundColor="#F5F5F5"
            modeLeft="black"
            modeRight="black"
          />
        }
      />
    </Document>
  );
};

export default BusinessPartnerPdfContent;
